import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getPublishedPOBManifestsForProject } from '@/services/db/pob';
import type { POBManifest } from '@/types/pob';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (manifest: POBManifest) => void;
  projectId: string;
}

export default function POBManifestSelectionModal({
  open,
  onClose,
  onSelect,
  projectId,
}: Props) {
  const { data: manifests = [], isLoading, error } = useQuery({
    queryKey: ['pob_published_manifests', projectId],
    queryFn: () => getPublishedPOBManifestsForProject(projectId),
    enabled: open && !!projectId,
    staleTime: 30 * 1000,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-mozaic-teal" />
            <h2 className="font-semibold text-mozaic-navy">
              Select a POB manifest to import
            </h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Published manifests for this project, newest first. Selecting one opens the
            per-company import step.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-mozaic-teal" />
            </div>
          )}
          {error && (
            <p className="text-sm text-mozaic-red">
              {error instanceof Error ? error.message : 'Failed to load manifests'}
            </p>
          )}
          {!isLoading && !error && manifests.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No published POB manifests for this project yet. Create one from the POB
              module.
            </p>
          )}
          {!isLoading && manifests.length > 0 && (
            <ul className="divide-y">
              {manifests.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(m);
                      onClose();
                    }}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-mozaic-navy">
                          {format(
                            new Date(m.manifest_date + 'T00:00:00'),
                            'EEE, MMM d, yyyy',
                          )}
                        </span>
                        <Badge className="bg-mozaic-teal/10 text-mozaic-teal border border-mozaic-teal/40">
                          {m.status}
                        </Badge>
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {m.facility_name ?? m.projects?.name ?? 'Facility'}
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0 text-right text-sm">
                      <div className="font-semibold text-mozaic-navy">
                        {m.total_pob ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">on board</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
